import type React from 'react';
import { createContext, createRef, useCallback, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { FollowUpQuestion } from './ai-tools/FollowUpQuestions/FollowUpQuestions';
import type { GuardianAgent } from './message-composer/chat-context/selectors';

/**
 * Which mode a conversation belongs to. It is a property of the session, not of the id — the
 * chat and the dashboard analysis each keep their own conversation, and the scope says which
 * one is live. It cannot be read from the route: this provider wraps the router.
 */
export type ConversationScope = 'chat' | 'dashboard';

export interface GuardianContextType {
  followUpQuestions: FollowUpQuestion[];
  setFollowUpQuestions: React.Dispatch<React.SetStateAction<FollowUpQuestion[]>>;
  lastChatCardRef: React.RefObject<HTMLDivElement | null>;
  /** The live conversation — the one belonging to the current scope. */
  conversationId: string;
  conversationScope: ConversationScope;
  /** Selects a conversation-mode chat (and switches the scope back to it). */
  setConversationId: React.Dispatch<React.SetStateAction<string>>;
  /** Selects the analysis conversation of a dashboard. */
  setDashboardConversationId: (conversationId: string) => void;
  /** Back to conversation mode, on the chat that was left behind. */
  resumeChatConversation: () => void;
  selectedAgent: GuardianAgent | null;
  setSelectedAgent: React.Dispatch<React.SetStateAction<GuardianAgent | null>>;
  isSidebarOpen: boolean;
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Desktop sidebar expand state — lives here so it survives route/layout changes. */
  isSidebarExpanded: boolean;
  setIsSidebarExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  draftMessage: string;
  setDraftMessage: React.Dispatch<React.SetStateAction<string>>;
}

export const GuardianContext = createContext<GuardianContextType>({
  followUpQuestions: [],
  setFollowUpQuestions: () => {},
  lastChatCardRef: createRef<HTMLDivElement>(),
  conversationId: '',
  conversationScope: 'chat',
  setConversationId: () => {},
  setDashboardConversationId: () => {},
  resumeChatConversation: () => {},
  selectedAgent: null,
  setSelectedAgent: () => {},
  isSidebarOpen: false,
  setIsSidebarOpen: () => {},
  isSidebarExpanded: false,
  setIsSidebarExpanded: () => {},
  draftMessage: '',
  setDraftMessage: () => {},
});

export interface GuardianProviderProps {
  children: React.ReactNode;
}

export const GuardianProvider = ({ children }: GuardianProviderProps) => {
  const [followUpQuestions, setFollowUpQuestions] = useState<FollowUpQuestion[]>([]);
  const lastChatCardRef = useRef<HTMLDivElement | null>(null);
  // One slot per mode, so a detour through a dashboard doesn't cost the user their chat.
  const [chatConversationId, setChatConversationId] = useState(uuidv4());
  const [dashboardConversationId, setDashboardConversationIdState] = useState<string | null>(null);
  const [conversationScope, setConversationScope] = useState<ConversationScope>('chat');
  const [selectedAgent, setSelectedAgent] = useState<GuardianAgent | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [draftMessage, setDraftMessage] = useState('');

  const conversationId =
    conversationScope === 'dashboard' && dashboardConversationId ? dashboardConversationId : chatConversationId;

  const setConversationId = useCallback((value: React.SetStateAction<string>) => {
    setChatConversationId(value);
    setConversationScope('chat');
    setSelectedAgent(null);
  }, []);

  // The dashboard picks its own agent right after, so this one leaves the selection alone.
  const setDashboardConversationId = useCallback((id: string) => {
    setDashboardConversationIdState(id);
    setConversationScope('dashboard');
  }, []);

  // Live mirror of the scope, so the callback below can read it without taking it as a dependency:
  // `ConversationLayout` uses that callback as an effect dependency, and a changing identity would
  // re-run the effect on every scope flip.
  const conversationScopeRef = useRef(conversationScope);
  conversationScopeRef.current = conversationScope;

  // Leaving dashboard mode drops its forced agent: `dashboard_agent` is synthetic and hidden from
  // the picker, so it means nothing in conversation mode. The chat's own agent comes back from the
  // conversation's metadata (`useRestoreSelectedAgent`), not from what the dashboard left here.
  const resumeChatConversation = useCallback(() => {
    if (conversationScopeRef.current === 'dashboard') setSelectedAgent(null);
    setConversationScope('chat');
  }, []);

  const ctxValue = useMemo(
    () => ({
      followUpQuestions,
      setFollowUpQuestions,
      lastChatCardRef,
      conversationId,
      conversationScope,
      setConversationId,
      setDashboardConversationId,
      resumeChatConversation,
      selectedAgent,
      setSelectedAgent,
      isSidebarOpen,
      setIsSidebarOpen,
      isSidebarExpanded,
      setIsSidebarExpanded,
      draftMessage,
      setDraftMessage,
    }),
    [
      followUpQuestions,
      conversationId,
      conversationScope,
      selectedAgent,
      setConversationId,
      setDashboardConversationId,
      resumeChatConversation,
      isSidebarOpen,
      isSidebarExpanded,
      draftMessage,
    ],
  );

  return <GuardianContext.Provider value={ctxValue}>{children}</GuardianContext.Provider>;
};
